import React, { useMemo, useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import StandardDropdown from '@wix/da-shared-react/pkg/Dropdown/Preset/StandardDropdown';
import VisuallyHidden from '@wix/da-shared-react/pkg/a11y/VisuallyHidden';
import MobileNativeDropdown from '@wix/da-shared-react/pkg/Dropdown/MobileNativeDropdown';
import { BiEventType, BiLink } from '@wix/da-shared-react/pkg/biLogger';
import { IconSize } from '@wix/da-shared-react/pkg/Icons/IconWrap';
import { MobileContext } from '@wix/da-shared-react/pkg/Context/MobileContext';
import { FilterOption } from '../../../types/api';
import s from './ContentTypeFilterDropdown.scss';

export interface Props {
  filterOptions?: FilterOption[];
  filter?: string;
  className?: string;
  menuClassName?: string;
  arrowDown?: (className: string) => React.ReactNode;
}

const ContentTypeFilterDropdown: React.FC<Props> = ({
  filterOptions = [],
  filter = '',
  className,
  menuClassName,
  arrowDown,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const items = useMemo(() => {
    return (filterOptions.filter(item => item) as NonNullable<
      FilterOption
    >[]).map(option => ({
      link: option.url,
      value: option.queryValue,
      label: option.title,
      selected: isMobile ? option.queryValue === filter : undefined,
      componentClass: BiLink,
      biData: {
        evid: BiEventType.SORT_TYPE_SELECT,
        menu_selection: option.queryValue || 'all',
        dropdown_type: 'content' as 'content',
      },
    }));
  }, [filterOptions, isMobile, filter]);
  const onMobileSelect = useCallback(
    selected => {
      const found = filterOptions?.find(item => item.queryValue === selected);
      if (found) {
        window.location.assign(found.url);
      }
    },
    [filterOptions]
  );

  if (!items.length) {
    return null;
  }

  if (isMobile) {
    return (
      <div className={classnames(s['filter-bar'], className)}>
        <label>
          <VisuallyHidden>{t('facetPage.a11y.orderDropdown')}</VisuallyHidden>
          <MobileNativeDropdown
            titleClassName={s['order-dropdown-title']}
            arrowClassName={s['order-dropdown-arrow']}
            title={
              filterOptions.find(option => option.queryValue === filter)
                ?.title ?? filterOptions[0].title
            }
            onSelect={onMobileSelect}
            items={items}
          />
        </label>
      </div>
    );
  }

  return (
    <StandardDropdown
      selectedValue={filter}
      items={items}
      className={classnames(s['dropdown'], className)}
      menuClassName={menuClassName}
      toggleClassName={classnames(
        s['facet-dropdown-toggle'],
        s['active-facet']
      )}
      renderArrowDown={arrowDown}
      arrowSize={IconSize.TINY}
    />
  );
};
ContentTypeFilterDropdown.displayName = 'ContentTypeFilterDropdown';

export default ContentTypeFilterDropdown;
